import { ESIGN_STATUSES, OTP_CHANNELS, PROTOCOL_SIGN_METHODS, type ProtocolSigningDto, todayInWarsaw } from '@bqcrm/shared';
import { Anchor, Badge, Button, FileButton, Group, Modal, SegmentedControl, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { IconBell, IconFileTypePdf, IconMail, IconPrinter, IconSignature } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api';
import { formatDateTime } from '../../format';
import { minHint } from '../../minHint';
import { notifyError, notifyOk } from '../../notify';

type Dialog = 'esign' | 'paper' | 'scan' | null;

/** Znacznik drogi podpisu protokołu (podpis zdalny: e-podpis / papier). */
export function SignMethodBadge({ s }: { s: Pick<ProtocolSigningDto, 'method' | 'status'> }) {
  if (s.status === 'do_podpisu') return <Badge size="sm" color="orange">Czeka na podpis klienta · {s.method === 'papier' ? 'papier' : 'e-podpis'}</Badge>;
  return (
    <Badge size="sm" color={s.method === 'epodpis' ? 'teal' : 'blue'}>
      {s.method === 'epodpis' ? 'Podpisany elektronicznie' : s.method === 'papier' ? 'Podpisany na papierze' : 'Podpisany na miejscu'}
    </Badge>
  );
}

/**
 * Podpis zdalny protokołu w biurze (klienta nie było przy odbiorze): kto, kiedy i którą drogą podpisał, ślad e-podpisu,
 * przypomnienie (nowy link), wysyłka do e-podpisu, zmiana na papier, wydruk do podpisu i odnotowanie podpisu ze skanem.
 */
export function ProtocolSigningPanel({ protocolId, code, email, signerName, s, onChanged }: { protocolId: string; code: string; email: string | null; signerName: string; s: ProtocolSigningDto; onChanged: () => void }) {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [form, setForm] = useState({ email: '', channel: 'email' as 'email' | 'sms', phone: '', signerName: '', reason: '', signedOn: todayInWarsaw(), remarks: '', file: null as File | null });
  const done = (msg: string) => {
    notifyOk(msg);
    setDialog(null);
    onChanged();
    void qc.invalidateQueries({ queryKey: ['case-process'] });
    void qc.invalidateQueries({ queryKey: ['case-timeline'] });
  };
  const run = useMutation({
    mutationFn: async (kind: 'remind' | 'esign' | 'paper' | 'scan') => {
      if (kind === 'remind') return api(`/protocols/${protocolId}/esign/remind`, { method: 'POST' });
      if (kind === 'esign') return api(`/protocols/${protocolId}/esign`, { method: 'POST', body: { email: form.email, channel: form.channel, phone: form.phone, signerName: form.signerName } });
      if (kind === 'paper') return api(`/protocols/${protocolId}/paper`, { method: 'POST', body: { reason: form.reason } });
      const fd = new FormData();
      fd.append('signedOn', form.signedOn);
      fd.append('signerName', form.signerName);
      fd.append('remarks', form.remarks);
      if (form.file) fd.append('scan', form.file);
      const res = await fetch(`/api/protocols/${protocolId}/paper/sign`, { method: 'POST', body: fd, credentials: 'same-origin' });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { message?: string; details?: string[] };
        throw new Error([data.message ?? 'Nie udało się zapisać podpisu', ...(data.details ?? [])].join('\n'));
      }
    },
    onSuccess: (_d, kind) =>
      done(kind === 'remind' ? 'Wysłano klientowi nowy link do podpisu' : kind === 'esign' ? 'Protokół wysłany do podpisu elektronicznego' : kind === 'paper' ? 'Protokół czeka na podpis papierowy' : 'Odnotowano podpis papierowy'),
    onError: notifyError,
  });
  const open = (d: Dialog) => {
    setForm({ email: s.esign?.email ?? email ?? '', channel: s.esign?.channel ?? 'email', phone: s.esign?.phone ?? '', signerName, reason: '', signedOn: todayInWarsaw(), remarks: '', file: null });
    setDialog(d);
  };
  const pending = s.status === 'do_podpisu';
  const e = s.esign;

  return (
    <Stack gap={4} mt={4}>
      <Group gap={6} wrap="wrap">
        <SignMethodBadge s={s} />
        {e && pending && <Badge size="sm" variant="light" color={e.status === 'w_toku' ? 'blue' : 'gray'}>{ESIGN_STATUSES[e.status]}</Badge>}
      </Group>
      {pending ? (
        <Text size="xs">
          Klienta nie było przy odbiorze — {s.method === 'papier' ? 'protokół do podpisu na wydruku (skan do systemu).' : 'protokół wysłany do podpisu elektronicznego.'} Odbiór zakresu i faktura końcowa czekają na podpis.
        </Text>
      ) : (
        <Text size="xs">
          Podpisał: {s.signerName}
          {s.signedAt ? `, ${formatDateTime(s.signedAt)}` : ''} — {PROTOCOL_SIGN_METHODS[s.method].toLowerCase()}
          {s.recordedByName ? ` (odnotował: ${s.recordedByName})` : ''}
        </Text>
      )}
      {e && (
        <Text size="xs" c="dimmed">
          E-podpis: {e.email}
          {e.channel === 'sms' ? `, kod SMS na ${e.phone}` : ', kod e-mailem'} · wysłano {formatDateTime(e.sentAt)}
          {e.lastLinkAt && e.lastLinkAt !== e.sentAt ? ` · ostatni link ${formatDateTime(e.lastLinkAt)}` : ''}
          {pending && e.status === 'w_toku' ? ` · ważny do ${formatDateTime(e.expiresAt)}` : ''} · otwarcia: {e.viewCount}
        </Text>
      )}
      {e && e.events.length > 0 && (
        <Text size="xs" c="dimmed">
          {e.events
            .slice(-4)
            .map((x) => `${formatDateTime(x.at)} ${x.label}`)
            .join(' · ')}
        </Text>
      )}
      {s.signerRemarks && <Text size="xs">Uwagi klienta przy podpisie: „{s.signerRemarks}”</Text>}
      {s.scan === 'jest' && (
        <Anchor href={`/api/protocols/${protocolId}/scan`} target="_blank" size="xs">
          <Group gap={2}>
            <IconFileTypePdf size={14} /> Skan podpisanego wydruku
          </Group>
        </Anchor>
      )}
      {s.scan === 'czeka_na_zdjecia' && (
        <Text size="xs" c="dimmed">
          Skan: czeka na zdjęcia z telefonu
        </Text>
      )}
      {pending && (
        <Group gap={4} wrap="wrap">
          {s.can.resend && (
            <Button size="compact-xs" leftSection={<IconBell size={14} />} loading={run.isPending && run.variables === 'remind'} onClick={() => run.mutate('remind')}>
              Przypomnij klientowi
            </Button>
          )}
          {s.can.send && (
            <Button size="compact-xs" variant="light" leftSection={<IconMail size={14} />} onClick={() => open('esign')}>
              {s.method === 'epodpis' ? 'Wyślij ponownie' : 'Wyślij do e-podpisu'}
            </Button>
          )}
          {s.can.print && (
            <Button size="compact-xs" variant="light" component="a" href={`/api/protocols/${protocolId}/print`} target="_blank" leftSection={<IconPrinter size={14} />}>
              Pobierz wydruk do podpisu
            </Button>
          )}
          {s.can.paper && s.method === 'epodpis' && (
            <Button size="compact-xs" variant="subtle" onClick={() => open('paper')}>
              Zmień na podpis papierowy
            </Button>
          )}
          {s.can.paper && (
            <Button size="compact-xs" variant="subtle" leftSection={<IconSignature size={14} />} onClick={() => open('scan')}>
              Odnotuj podpis papierowy
            </Button>
          )}
        </Group>
      )}

      <Modal opened={dialog === 'esign'} onClose={() => setDialog(null)} title={`Wysyłka do podpisu elektronicznego ${code}`}>
        <Stack>
          <Text size="sm">Klient dostanie e-mail z linkiem: przeczyta protokół, może wpisać uwagi i podpisze go jednorazowym kodem. Poprzedni link przestanie działać.</Text>
          <TextInput label="Imię i nazwisko klienta" required value={form.signerName} onChange={(x) => setForm({ ...form, signerName: x.currentTarget.value })} />
          <TextInput label="Adres e-mail klienta" required type="email" value={form.email} onChange={(x) => setForm({ ...form, email: x.currentTarget.value })} />
          <SegmentedControl data={Object.entries(OTP_CHANNELS).map(([value, label]) => ({ value, label: `Kod: ${label}` }))} value={form.channel} onChange={(v) => setForm({ ...form, channel: v as 'email' | 'sms' })} />
          {form.channel === 'sms' && <TextInput label="Telefon do kodu SMS" required value={form.phone} onChange={(x) => setForm({ ...form, phone: x.currentTarget.value })} />}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDialog(null)}>
              Anuluj
            </Button>
            <Button loading={run.isPending} disabled={form.signerName.trim().length < 3 || !form.email.includes('@') || (form.channel === 'sms' && form.phone.trim().length < 9)} onClick={() => run.mutate('esign')}>
              Wyślij do podpisu
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={dialog === 'paper'} onClose={() => setDialog(null)} title={`Podpis papierowy ${code}`}>
        <Stack>
          <Text size="sm">Link do podpisu elektronicznego przestanie działać, klient dostanie informację. Protokół podpisze na wydruku — potem odnotuj podpis ze skanem.</Text>
          <TextInput label="Powód" required description={minHint(form.reason)} value={form.reason} onChange={(x) => setForm({ ...form, reason: x.currentTarget.value })} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDialog(null)}>
              Anuluj
            </Button>
            <Button loading={run.isPending} disabled={form.reason.trim().length < 3} onClick={() => run.mutate('paper')}>
              Zmień na papier
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={dialog === 'scan'} onClose={() => setDialog(null)} title={`Odnotowanie podpisu ${code}`}>
        <Stack>
          <TextInput label="Data podpisu" type="date" max={todayInWarsaw()} value={form.signedOn} onChange={(x) => setForm({ ...form, signedOn: x.currentTarget.value })} />
          <TextInput label="Kto podpisał (klient)" required value={form.signerName} onChange={(x) => setForm({ ...form, signerName: x.currentTarget.value })} />
          <Textarea label="Uwagi klienta z wydruku" description="Puste — bez uwag. Uwagi trafią do rozpatrzenia przez koordynatora." autosize minRows={2} value={form.remarks} onChange={(x) => setForm({ ...form, remarks: x.currentTarget.value })} />
          <Group>
            <FileButton onChange={(f) => setForm({ ...form, file: f })} accept="application/pdf,image/jpeg,image/png">
              {(props) => (
                <Button {...props} variant="light">
                  Wybierz skan (PDF / zdjęcie)
                </Button>
              )}
            </FileButton>
            <Text size="sm">{form.file?.name ?? 'brak pliku'}</Text>
          </Group>
          <Text size="xs" c="dimmed">
            Po zapisaniu protokół jest podpisany: zakres odebrany, faktura końcowa i zadania sprawy jak po podpisie na miejscu.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDialog(null)}>
              Anuluj
            </Button>
            <Button loading={run.isPending} disabled={!form.file || form.signerName.trim().length < 3 || !form.signedOn} onClick={() => run.mutate('scan')}>
              Zapisz podpis
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
